import { Avatar, Text } from '@mantine/core';
import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CopilotMessageItem } from 'app/modules/wallforming/components/copilot/CopilotMessageItem';
import engCopilot from 'shared/components/assets/engCopilot.jpg';

import type {
    CopilotLiveActivity,
    CopilotMessage,
    CopilotMode,
    CopilotProposal
} from 'app/modules/wallforming/components/copilot/types/copilotTypes';

interface CopilotMessageListProps {
    activeConversationId?: string;
    imageSmall?: string;
    isApplying: boolean;
    isSending: boolean;
    liveActivity?: CopilotLiveActivity | null;
    messages: CopilotMessage[];
    mode: CopilotMode;
    onApplyProposal: (proposal: CopilotProposal) => void;
    onCopyMessage: (content: string, renderedContent?: HTMLElement | null) => void;
    onRejectProposal: (proposalId: string) => void;
}

export const CopilotMessageList = memo(function CopilotMessageList({
    activeConversationId,
    imageSmall,
    isApplying,
    isSending,
    liveActivity,
    messages,
    mode,
    onApplyProposal,
    onCopyMessage,
    onRejectProposal
}: CopilotMessageListProps) {
    const { t } = useTranslation();
    const chatContentRef = useRef<HTMLDivElement | null>(null);
    const liveReasoningRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const chatContent = chatContentRef.current;
        if (chatContent) {
            chatContent.scrollTop = chatContent.scrollHeight;
        }
        const liveReasoning = liveReasoningRef.current;
        if (liveReasoning) {
            liveReasoning.scrollTop = liveReasoning.scrollHeight;
        }
    }, [activeConversationId, isSending, liveActivity, messages.length]);

    return (
        <div ref={chatContentRef} className="mb-4 mt-4 min-h-0 flex-1 overflow-y-auto bg-background-default px-3 py-4">
            <div aria-live="polite" className="flex flex-col gap-4">
                {messages.length === 0 && (
                    <div className="mx-auto my-auto max-w-sm px-4 py-10 text-center">
                        <img
                            alt=""
                            className="mx-auto mb-4 h-auto max-h-48 max-w-full object-contain"
                            height={192}
                            src={engCopilot}
                            width={128}
                        />
                        <Text className="text-md" fw={600}>
                            {t('copilotWelcome')}
                        </Text>
                        <Text c="dimmed" className="text-sm" mt={4}>
                            {t('copilotWelcomeDescription')}
                        </Text>
                    </div>
                )}
                {messages.map((message, index) => (
                    <CopilotMessageItem
                        key={`${message.role}-${index}`}
                        imageSmall={imageSmall}
                        isApplying={isApplying}
                        isSending={isSending}
                        message={message}
                        onApplyProposal={onApplyProposal}
                        onCopyMessage={onCopyMessage}
                        onRejectProposal={onRejectProposal}
                    />
                ))}
                {isSending && (
                    <div
                        className="flex items-start gap-2 text-text-placeholder"
                        data-testid="copilot-live-activity"
                        role="status"
                    >
                        <Avatar aria-hidden="true" className="h-7 w-7 shrink-0" src={engCopilot} />
                        <div className="min-w-0 flex-1 space-y-1">
                            <Text c="dimmed" size="xs">
                                {liveActivity?.tools.length
                                    ? t('copilotRunningTool', {
                                          tool: liveActivity.tools[liveActivity.tools.length - 1]
                                      })
                                    : liveActivity?.status ||
                                      t(mode === 'agent' ? 'copilotAgentThinking' : 'copilotAskThinking')}
                            </Text>
                            {liveActivity?.reasoning && (
                                <div
                                    ref={liveReasoningRef}
                                    aria-label={t('labelModelReasoning')}
                                    className="max-h-40 overflow-auto whitespace-pre-wrap border-l-2 border-border-light pl-2 text-xs italic"
                                    data-testid="copilot-live-reasoning"
                                >
                                    {liveActivity.reasoning}
                                </div>
                            )}
                            {liveActivity?.answer && (
                                <div
                                    className="whitespace-pre-wrap text-sm text-text-default"
                                    data-testid="copilot-live-answer"
                                >
                                    {liveActivity.answer}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});
